<script lang="ts">
import { useSignInEmailPassword, useChangePassword } from '@nhost/vue';
import { useLocale } from 'vuetify';
import { useClientStore } from '@/store/client';
import { gql } from '@apollo/client/core';
import nhost from '@/plugins/nhost';

// Lightweight UUID validator to avoid extra runtime dependencies here
const isUuid = (value: string) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const GET_WHITE_LABEL_LOGIN_DATA = gql`
  query GetWhiteLabelLoginData($domain: String!) {
    general_module_configuration_dataroom(where: { custom_domain: { _eq: $domain } }) {
      logo_url
      primary_color
      secondary_color
    }
  }
`;

export default {
  setup() {
    const clientStore = useClientStore();
    const { t } = useLocale();
    const getTranslation = (path: string) => t(path);
    return {
      getTranslation,
      clientStore,
    };
  },
  data() {
    return {
      // layout / branding
      loading: true,
      logoUrl: '@/assets/logo_cockpit_full_svg.svg',
      logoImageData: null as any,

      password: '',
      passwordConfirm: '',
      needsEmailVerification: false,
      visible: false,
      rememberMe: false,
      passwordMismatchAlert: false,
      changePassword: undefined,
    } as any;
  },
  created() {
    const { user } = useSignInEmailPassword();
    if (!user.value?.id) this.$router.push('/forgot-password');
    const { changePassword } = useChangePassword();
    this.changePassword = changePassword as any;
  },
  methods: {
    async handleSubmit(event: Event) {
      event.preventDefault();
      this.passwordMismatchAlert = false;

      if (this.password !== this.passwordConfirm) {
        this.passwordMismatchAlert = true;
      } else {
        const { isSuccess } = await this.changePassword(this.password);

        if (isSuccess) {
          this.$router.push('/app');
        }
      }
    },
    async handleForgotPassword(event: Event) {
      this.$router.push('/forgot-password');
    },

    async imageToBase64(url: string): Promise<string> {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Important for CORS

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }

            ctx.drawImage(img, 0, 0);
            const base64 = canvas.toDataURL('image/png');
            resolve(base64);
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = url;
      });
    },

    async preloadImage(uuid: string, imageDataKey: string) {
      try {
        // 1. Try to load from localStorage
        const cached = localStorage.getItem(`logoBase64_${uuid}`);
        if (cached && cached !== 'null') {
          (this as any)[imageDataKey] = cached;
          return;
        }

        // 2. Fetch presigned URL and convert to base64
        const publicUrl = await nhost.storage.getPresignedUrl({ fileId: uuid });
        if (!publicUrl.presignedUrl) return;
        let url = publicUrl.presignedUrl.url;
        if (url && !/^https?:\/\//.test(url)) url = 'http://' + url;
        const base64Data = await this.imageToBase64(url);

        localStorage.setItem(`logoBase64_${uuid}`, base64Data);
        (this as any)[imageDataKey] = base64Data;
      } catch (error) {
        console.error('Error preloading image:', error);
      }
    },

    async getLogoUrl(logo_url: string) {
      if (!logo_url) return '@/assets/logo_cockpit_full_svg.svg';
      if (!isUuid(logo_url)) {
        return logo_url;
      }

      await this.preloadImage(logo_url, 'logoImageData');
      return this.logoImageData;
    },
  },
  apollo: {
    whiteLabelLoginData: {
      query: GET_WHITE_LABEL_LOGIN_DATA,
      variables: {
        domain: window.location.hostname,
      },
      async update(data) {
        if (data.general_module_configuration_dataroom[0]?.logo_url) {
          this.logoUrl = await this.getLogoUrl(
            data.general_module_configuration_dataroom[0].logo_url,
          );
          this.clientStore.setSelectedClientId(
            data.general_module_configuration_dataroom[0].client_id,
          );
        }
        this.loading = false;
        return data.general_module_configuration_dataroom[0];
      },
    },
  },
};
</script>

<template>
  <div class="change-password pa-10 d-flex">
    <div class="w-75" v-if="!loading">
      <div class="d-flex justify-center">
        <v-img eager class="mb-10 brand-logo" :src="logoUrl" />
      </div>
      <div class="justify-start">
        <h3>{{ getTranslation('$vuetify.auth.change-password.message.title') }}</h3>
        <p class="change-password-text-body">{{ getTranslation('$vuetify.auth.change-password.message.text') }}</p>
      </div>
      <br />

      <v-form @submit="handleSubmit">
        <v-text-field
          :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
          :type="visible ? 'text' : 'password'"
          variant="outlined"
          v-model="password"
          @click:append-inner="visible = !visible"
          :label="getTranslation('$vuetify.auth.change-password.password.label')"
        />
        <br />
        <v-text-field
          :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
          :type="visible ? 'text' : 'password'"
          variant="outlined"
          v-model="passwordConfirm"
          @click:append-inner="visible = !visible"
          :label="getTranslation('$vuetify.auth.change-password.confirmPassword.label')"
        />
        <br />
        <v-alert
          v-model="passwordMismatchAlert"
          type="error"
          variant="tonal"
          color="danger"
          border="start"
          closable
          :text="getTranslation('$vuetify.auth.change-password.password-mismatch-alert.text')"
        />
        <br />
        <v-btn min-width="100%" color="primary" class="text-none" type="submit">
          <span class="text-white">
            {{ getTranslation('$vuetify.auth.change-password.confirm-btn.text') }}
          </span>
        </v-btn>
        <br />
      </v-form>
    </div>

    <div class="w-75" v-else>
      <div class="d-flex justify-center">
        <v-progress-circular indeterminate color="primary" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.change-password {
  justify-content: center;
  align-items: center;
  text-align: center;
}

.forgot-password-text {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}

.change-password-text-body {
  color: rgb(var(--v-theme-text-body-secondary));
}

.brand-logo {
  max-width: 500px;         /* fixed logo box */
  max-height: 300px;
  object-fit: contain;  /* keep aspect ratio inside box */
}
</style>
